import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabase";
import { buildRewardsData } from "@/hooks/useRewards";

interface CustomerPoints {
  id: string;
  name: string;
  phone: string;
  points: number;
  totalPointsEarned: number;
  tier: string;
  tierLabel: string;
  tierColor: string;
  ordersCount: number;
  lastOrderDate: string | null;
}

interface PointsStats {
  totalPointsGranted: number;
  totalActivePoints: number;
  totalCustomersWithPoints: number;
  platinumCount: number;
  goldCount: number;
  silverCount: number;
  bronzeCount: number;
  avgPointsPerCustomer: number;
  pointsThisMonth: number;
}

const TIER_CONFIG: Record<string, { label: string; color: string; bg: string; icon: string; border: string }> = {
  platinum: { label: "بلاتيني", color: "text-cyan-600",   bg: "bg-cyan-50",   icon: "ri-vip-diamond-line",  border: "border-cyan-200" },
  gold:     { label: "ذهبي",    color: "text-amber-600",  bg: "bg-amber-50",  icon: "ri-vip-crown-line",    border: "border-amber-200" },
  silver:   { label: "فضي",     color: "text-gray-500",   bg: "bg-gray-50",   icon: "ri-medal-2-line",      border: "border-gray-200" },
  bronze:   { label: "برونزي",  color: "text-orange-600", bg: "bg-orange-50", icon: "ri-medal-line",        border: "border-orange-200" },
};

interface Props {
  onToast: (msg: string, type?: "success" | "error") => void;
}

export default function PointsReport({ onToast }: Props) {
  const [customers, setCustomers] = useState<CustomerPoints[]>([]);
  const [stats, setStats] = useState<PointsStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [tierFilter, setTierFilter] = useState<string>("all");
  const [sortBy, setSortBy] = useState<"points" | "earned" | "orders">("points");

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch all customers with points data
      const { data: custData, error: custErr } = await supabase
        .from("customers")
        .select("id, name, phone, points, total_points_earned")
        .order("points", { ascending: false });

      if (custErr) throw custErr;

      // Fetch orders per customer (count + last date + points earned this month)
      const { data: ordersData } = await supabase
        .from("orders")
        .select("customer_id, created_at, points_earned, status")
        .not("customer_id", "is", null);

      const ordersByCustomer: Record<string, { count: number; lastDate: string | null; monthlyPoints: number }> = {};
      const now = new Date();
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();

      (ordersData ?? []).forEach((o) => {
        if (!o.customer_id) return;
        if (!ordersByCustomer[o.customer_id]) {
          ordersByCustomer[o.customer_id] = { count: 0, lastDate: null, monthlyPoints: 0 };
        }
        ordersByCustomer[o.customer_id].count++;
        if (!ordersByCustomer[o.customer_id].lastDate || o.created_at > ordersByCustomer[o.customer_id].lastDate!) {
          ordersByCustomer[o.customer_id].lastDate = o.created_at;
        }
        if (o.created_at >= monthStart && o.points_earned > 0) {
          ordersByCustomer[o.customer_id].monthlyPoints += o.points_earned;
        }
      });

      const mappedCustomers: CustomerPoints[] = (custData ?? []).map((c) => {
        const pts = c.points ?? 0;
        const rewards = buildRewardsData(pts, c.total_points_earned ?? 0);
        const orderInfo = ordersByCustomer[c.id] ?? { count: 0, lastDate: null, monthlyPoints: 0 };
        return {
          id: c.id,
          name: c.name ?? "بدون اسم",
          phone: c.phone ?? "",
          points: pts,
          totalPointsEarned: c.total_points_earned ?? 0,
          tier: rewards.tier,
          tierLabel: rewards.tierLabel,
          tierColor: rewards.tierColor,
          ordersCount: orderInfo.count,
          lastOrderDate: orderInfo.lastDate,
        };
      }).filter((c) => c.points > 0 || c.totalPointsEarned > 0);

      // Compute stats
      const totalGranted = (ordersData ?? []).reduce((s, o) => s + (o.points_earned ?? 0), 0);
      const totalActive = mappedCustomers.reduce((s, c) => s + c.points, 0);
      const pointsThisMonth = Object.values(ordersByCustomer).reduce((s, o) => s + o.monthlyPoints, 0);

      setCustomers(mappedCustomers);
      setStats({
        totalPointsGranted: totalGranted,
        totalActivePoints: totalActive,
        totalCustomersWithPoints: mappedCustomers.length,
        platinumCount: mappedCustomers.filter((c) => c.tier === "platinum").length,
        goldCount:     mappedCustomers.filter((c) => c.tier === "gold").length,
        silverCount:   mappedCustomers.filter((c) => c.tier === "silver").length,
        bronzeCount:   mappedCustomers.filter((c) => c.tier === "bronze").length,
        avgPointsPerCustomer: mappedCustomers.length
          ? Math.round(totalActive / mappedCustomers.length)
          : 0,
        pointsThisMonth,
      });
    } catch (err) {
      console.error(err);
      onToast("حدث خطأ أثناء تحميل بيانات النقاط", "error");
    }
    setLoading(false);
  }, [onToast]);

  useEffect(() => { loadData(); }, [loadData]);

  // Filtered & sorted
  const filtered = customers
    .filter((c) => {
      const matchSearch = c.name.includes(search) || c.phone.includes(search);
      const matchTier = tierFilter === "all" || c.tier === tierFilter;
      return matchSearch && matchTier;
    })
    .sort((a, b) => {
      if (sortBy === "points") return b.points - a.points;
      if (sortBy === "earned") return b.totalPointsEarned - a.totalPointsEarned;
      return b.ordersCount - a.ordersCount;
    });

  const exportCSV = useCallback(() => {
    const headers = ["الترتيب", "الاسم", "الجوال", "المستوى", "النقاط الحالية", "إجمالي النقاط المكتسبة", "عدد الطلبات", "آخر طلب"];
    const rows = filtered.map((c, i) => [
      i + 1, c.name, c.phone, c.tierLabel, c.points, c.totalPointsEarned, c.ordersCount,
      c.lastOrderDate ? new Date(c.lastOrderDate).toLocaleDateString("ar-SA") : "—",
    ].map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","));
    const bom = "\uFEFF";
    const csv = bom + [headers.map((h) => `"${h}"`).join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "تقرير_النقاط.csv";
    a.click();
    URL.revokeObjectURL(url);
    onToast("تم تصدير تقرير النقاط ✓");
  }, [filtered, onToast]);

  return (
    <div className="flex flex-col gap-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="font-black text-[#1a1a1a] text-lg flex items-center gap-2">
            <i className="ri-vip-crown-line text-amber-500"></i>
            تقرير النقاط والمكافآت
          </h2>
          <p className="text-gray-400 text-xs mt-0.5">إجمالي النقاط الممنوحة وأكثر العملاء نقاطاً</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={loadData}
            className="w-9 h-9 flex items-center justify-center border border-gray-200 rounded-xl text-gray-400 hover:text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
          >
            <i className="ri-refresh-line text-sm"></i>
          </button>
          <button
            onClick={exportCSV}
            disabled={filtered.length === 0}
            className="flex items-center gap-2 bg-[#1a1a1a] hover:bg-[#333] disabled:opacity-50 text-white font-bold text-sm px-4 py-2 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-download-2-line text-base"></i>
            تصدير CSV
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center py-24 gap-3 text-gray-400">
          <i className="ri-loader-4-line text-3xl animate-spin"></i>
          <p className="text-sm">جاري تحميل بيانات النقاط...</p>
        </div>
      ) : (
        <>
          {/* KPI Cards */}
          {stats && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {/* Total Granted */}
              <div className="bg-[#1a1a1a] rounded-xl p-4">
                <div className="w-8 h-8 flex items-center justify-center bg-white/10 rounded-lg mb-3">
                  <i className="ri-gift-line text-white text-sm"></i>
                </div>
                <p className="text-2xl font-black text-white">{stats.totalPointsGranted.toLocaleString("ar-SA")}</p>
                <p className="text-xs text-white/50 mt-0.5">إجمالي النقاط الممنوحة</p>
              </div>

              {/* Active Points */}
              <div className="bg-white rounded-xl p-4 border border-gray-100">
                <div className="w-8 h-8 flex items-center justify-center bg-amber-50 rounded-lg mb-3">
                  <i className="ri-coin-line text-amber-600 text-sm"></i>
                </div>
                <p className="text-2xl font-black text-[#1a1a1a]">{stats.totalActivePoints.toLocaleString("ar-SA")}</p>
                <p className="text-xs text-gray-400 mt-0.5">النقاط النشطة حالياً</p>
              </div>

              {/* Customers with Points */}
              <div className="bg-white rounded-xl p-4 border border-gray-100">
                <div className="w-8 h-8 flex items-center justify-center bg-green-50 rounded-lg mb-3">
                  <i className="ri-group-line text-green-600 text-sm"></i>
                </div>
                <p className="text-2xl font-black text-[#1a1a1a]">{stats.totalCustomersWithPoints}</p>
                <p className="text-xs text-gray-400 mt-0.5">عملاء لديهم نقاط</p>
              </div>

              {/* This Month */}
              <div className="bg-white rounded-xl p-4 border border-gray-100">
                <div className="w-8 h-8 flex items-center justify-center bg-teal-50 rounded-lg mb-3">
                  <i className="ri-calendar-line text-teal-600 text-sm"></i>
                </div>
                <p className="text-2xl font-black text-[#1a1a1a]">{stats.pointsThisMonth.toLocaleString("ar-SA")}</p>
                <p className="text-xs text-gray-400 mt-0.5">نقاط هذا الشهر</p>
              </div>
            </div>
          )}

          {/* Tier Distribution */}
          {stats && (
            <div className="bg-white rounded-xl border border-gray-100 p-5">
              <p className="text-sm font-bold text-[#1a1a1a] mb-4 flex items-center gap-2">
                <i className="ri-bar-chart-horizontal-line text-gray-400 text-sm"></i>
                توزيع المستويات
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { key: "platinum", count: stats.platinumCount },
                  { key: "gold",     count: stats.goldCount },
                  { key: "silver",   count: stats.silverCount },
                  { key: "bronze",   count: stats.bronzeCount },
                ].map(({ key, count }) => {
                  const cfg = TIER_CONFIG[key];
                  const total = stats.totalCustomersWithPoints || 1;
                  const pct = Math.round((count / total) * 100);
                  return (
                    <button
                      key={key}
                      onClick={() => setTierFilter(tierFilter === key ? "all" : key)}
                      className={`p-3 rounded-xl border transition-all cursor-pointer text-right ${
                        tierFilter === key
                          ? `${cfg.bg} ${cfg.border} ring-2 ring-offset-1 ring-current ${cfg.color}`
                          : "bg-white border-gray-100 hover:border-gray-200"
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className={`w-8 h-8 flex items-center justify-center ${cfg.bg} rounded-lg`}>
                          <i className={`${cfg.icon} ${cfg.color} text-sm`}></i>
                        </div>
                        <span className={`text-xl font-black ${cfg.color}`}>{count}</span>
                      </div>
                      <p className="text-xs font-bold text-[#1a1a1a]">{cfg.label}</p>
                      <div className="mt-2 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all ${
                            key === "platinum" ? "bg-cyan-400" :
                            key === "gold" ? "bg-amber-400" :
                            key === "silver" ? "bg-gray-400" : "bg-orange-400"
                          }`}
                          style={{ width: `${pct}%` }}
                        ></div>
                      </div>
                      <p className="text-[10px] text-gray-400 mt-1">{pct}% من العملاء</p>
                    </button>
                  );
                })}
              </div>
              {tierFilter !== "all" && (
                <button
                  onClick={() => setTierFilter("all")}
                  className="mt-3 text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1 cursor-pointer transition-colors"
                >
                  <i className="ri-close-circle-line text-sm"></i>
                  إلغاء فلتر {TIER_CONFIG[tierFilter]?.label}
                </button>
              )}
            </div>
          )}

          {/* Top Customers Table */}
          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            {/* Table Controls */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 px-5 py-4 border-b border-gray-100">
              <p className="text-sm font-bold text-[#1a1a1a] flex items-center gap-2 flex-1">
                <i className="ri-trophy-line text-amber-500 text-sm"></i>
                أكثر العملاء نقاطاً
                <span className="text-xs text-gray-400 font-normal">({filtered.length} عميل)</span>
              </p>
              <div className="flex items-center gap-2 w-full sm:w-auto">
                {/* Search */}
                <div className="relative flex-1 sm:w-48">
                  <i className="ri-search-line absolute top-1/2 -translate-y-1/2 right-3 text-gray-300 text-sm"></i>
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="بحث بالاسم أو الجوال..."
                    className="w-full border border-gray-200 rounded-lg pr-8 pl-3 py-2 text-xs outline-none focus:border-gray-400 transition-colors"
                  />
                </div>
                {/* Sort */}
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
                  className="border border-gray-200 rounded-lg px-3 py-2 text-xs font-medium text-gray-600 outline-none focus:border-gray-400 cursor-pointer bg-white"
                >
                  <option value="points">ترتيب: النقاط الحالية</option>
                  <option value="earned">ترتيب: إجمالي المكتسبة</option>
                  <option value="orders">ترتيب: عدد الطلبات</option>
                </select>
              </div>
            </div>

            {filtered.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-gray-300 gap-3">
                <i className="ri-vip-crown-line text-4xl"></i>
                <p className="text-sm">
                  {search || tierFilter !== "all" ? "لا توجد نتائج مطابقة" : "لا يوجد عملاء لديهم نقاط بعد"}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-100">
                      {["#", "العميل", "المستوى", "النقاط الحالية", "إجمالي المكتسب", "الطلبات", "آخر طلب"].map((h) => (
                        <th key={h} className="text-right text-[11px] font-bold text-gray-400 px-4 py-3 whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((c, idx) => {
                      const cfg = TIER_CONFIG[c.tier];
                      const isTop3 = idx < 3;
                      return (
                        <tr
                          key={c.id}
                          className={`border-b border-gray-50 hover:bg-gray-50/50 transition-colors ${idx % 2 === 0 ? "" : "bg-gray-50/20"}`}
                        >
                          {/* Rank */}
                          <td className="px-4 py-3">
                            {isTop3 ? (
                              <div className={`w-7 h-7 flex items-center justify-center rounded-full font-black text-xs ${
                                idx === 0 ? "bg-amber-400 text-black" :
                                idx === 1 ? "bg-gray-300 text-gray-700" :
                                "bg-orange-300 text-orange-800"
                              }`}>
                                {idx + 1}
                              </div>
                            ) : (
                              <span className="text-xs text-gray-400 font-bold px-2">{idx + 1}</span>
                            )}
                          </td>

                          {/* Customer */}
                          <td className="px-4 py-3">
                            <p className="text-xs font-bold text-[#1a1a1a] whitespace-nowrap">{c.name}</p>
                            <p className="text-[10px] text-gray-400 mt-0.5" dir="ltr">{c.phone}</p>
                          </td>

                          {/* Tier */}
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-full ${cfg.bg} ${cfg.color} whitespace-nowrap`}>
                              <i className={`${cfg.icon} text-xs`}></i>
                              {cfg.label}
                            </span>
                          </td>

                          {/* Current Points */}
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1.5">
                              <span className="text-sm font-black text-[#1a1a1a]">
                                {c.points.toLocaleString("ar-SA")}
                              </span>
                              <span className="text-[10px] text-gray-400">نقطة</span>
                            </div>
                            {/* Mini progress bar toward next tier */}
                            {(() => {
                              const tiers = [
                                { min: 0, max: 100 }, { min: 100, max: 300 },
                                { min: 300, max: 600 }, { min: 600, max: 600 }
                              ];
                              const t = c.tier === "bronze" ? tiers[0] : c.tier === "silver" ? tiers[1] : c.tier === "gold" ? tiers[2] : tiers[3];
                              const pct = t.max === t.min ? 100 : Math.min(100, Math.round(((c.points - t.min) / (t.max - t.min)) * 100));
                              if (c.tier === "platinum") return null;
                              return (
                                <div className="mt-1.5 w-16 h-1 bg-gray-100 rounded-full overflow-hidden">
                                  <div
                                    className={`h-full rounded-full ${
                                      c.tier === "bronze" ? "bg-orange-400" :
                                      c.tier === "silver" ? "bg-gray-400" : "bg-amber-400"
                                    }`}
                                    style={{ width: `${pct}%` }}
                                  ></div>
                                </div>
                              );
                            })()}
                          </td>

                          {/* Total Earned */}
                          <td className="px-4 py-3">
                            <span className="text-xs font-bold text-gray-600">
                              {c.totalPointsEarned.toLocaleString("ar-SA")}
                            </span>
                          </td>

                          {/* Orders */}
                          <td className="px-4 py-3">
                            <span className="text-xs font-bold text-gray-600">{c.ordersCount}</span>
                          </td>

                          {/* Last Order */}
                          <td className="px-4 py-3">
                            <span className="text-xs text-gray-500 whitespace-nowrap">
                              {c.lastOrderDate
                                ? new Date(c.lastOrderDate).toLocaleDateString("ar-SA", { year: "numeric", month: "short", day: "numeric" })
                                : "—"}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Footer avg */}
            {stats && filtered.length > 0 && (
              <div className="flex items-center justify-between px-5 py-3 bg-gray-50/50 border-t border-gray-100">
                <p className="text-xs text-gray-400">
                  متوسط النقاط لكل عميل:
                  <span className="font-bold text-[#1a1a1a] mr-1">{stats.avgPointsPerCustomer.toLocaleString("ar-SA")} نقطة</span>
                </p>
                <p className="text-xs text-gray-400">
                  إجمالي النقاط النشطة:
                  <span className="font-bold text-amber-600 mr-1">{stats.totalActivePoints.toLocaleString("ar-SA")}</span>
                </p>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
